import * as Yup from 'yup';
import { Amplify } from 'aws-amplify';
import { PANTHER_CONFIG } from 'Source/constants';
import dayjs from 'dayjs';
import relativeTimePlugin from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { LicenseInfo } from '@mui/x-license';

// Initialize MUI X License
LicenseInfo.setLicenseKey(
  '6ae0bbdf2eaff89a56c2cb3051123b71Tz0xMjI3OTAsRT0xNzk2NTE1MTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=',
);

// Extend dayjs to allow to display relative times (i.e. "5 seconds ago")
dayjs.extend(relativeTimePlugin);

// Extend dayjs to allow for better conversions between UTC and local time
dayjs.extend(utc);

// Extend dayjs to allow for timezone functions
dayjs.extend(timezone);

// Initialize Amplify with Cognito configuration
const amplifyConfig = {
  Auth: {
    Cognito: {
      region: PANTHER_CONFIG.AWS_REGION,
      userPoolId: PANTHER_CONFIG.WEB_APPLICATION_USER_POOL_ID,
      userPoolClientId: PANTHER_CONFIG.WEB_APPLICATION_USER_POOL_CLIENT_ID,
    },
  },
};
Amplify.configure(amplifyConfig);

// Set the defaults for some of the pre-implemented yup funcs
Yup.setLocale({
  mixed: {
    required: 'This field is required',
    notType: 'Must have a value',
  },
});

// Add a custom `unique` method on Yup that's gonna validate that an array of items doesn't contain
// duplicates. The duplicates can be entire items or only a certain field (based on the `mapper` param
// that's passed.
Yup.addMethod(Yup.array, 'unique', function method(this, message = 'No duplicates allowed', key) {
  return this.test('unique', message, function testFunc(items) {
    // Find all duplicates (comparing trimmed values for strings)
    const seen = new Map();
    const duplicateIndices = [];

    items.forEach((item, index) => {
      let value = key ? item[key] : item;
      // Trim strings for comparison
      if (typeof value === 'string') {
        value = value.trim();
      }

      if (seen.has(value)) {
        duplicateIndices.push(index);
        // Also mark the first occurrence if not already marked
        const firstIndex = seen.get(value);
        if (!duplicateIndices.includes(firstIndex)) {
          duplicateIndices.push(firstIndex);
        }
      } else {
        seen.set(value, index);
      }
    });

    if (duplicateIndices.length > 0) {
      // Create error on the first duplicate index found
      const lastDuplicateIndex = Math.max(...duplicateIndices);
      const errorPath = key
        ? `${this.path}[${lastDuplicateIndex}].${key}`
        : `${this.path}[${lastDuplicateIndex}]`;
      return this.createError({ path: errorPath, message });
    }
    return true;
  });
});

// Add a custom 'integer' method that validates a string is actually a digits only string
Yup.addMethod(
  Yup.string,
  'integer',
  function method(this, message = 'The field should have digits only') {
    return this.matches(/^\d+$/, message);
  },
);

// Overriding requestIdleCallback because Safari is not supporting it
// @ts-ignore
window.requestIdleCallback = window.requestIdleCallback || (cb => cb());
